import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Clock, LogOut, Send, Paperclip, X, CheckCircle, Loader } from 'lucide-react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import './PendingApproval.css';

const PendingApproval = () => {
  const { signOut, currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async () => {
    if (!message.trim() && !file) return;
    setSending(true);

    try {
      let fileData = { fileName: null, fileSize: null, fileType: null, fileURL: null, storagePath: null };

      if (file) {
        const path = `chats/${currentUser.uid}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, file);
          task.on('state_changed',
            snap => setUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
            reject,
            async () => {
              fileData = {
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                fileURL: await getDownloadURL(task.snapshot.ref),
                storagePath: path,
              };
              resolve();
            }
          );
        });
      }

      await addDoc(collection(db, 'messages'), {
        chatId: currentUser.uid,
        senderId: currentUser.uid,
        senderName: currentUser.displayName || currentUser.email,
        senderRole: 'client',
        content: message.trim(),
        createdAt: Timestamp.now(),
        read: false,
        ...fileData,
      });

      setMessage('');
      setFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pending-container">
      <div className="pending-card">
        <div className="pending-icon">
          <Clock size={48} />
        </div>

        <h1>Account Pending Approval</h1>

        <div className="pending-message">
          <p>
            Thank you for registering, <strong>{currentUser?.displayName}</strong>.
          </p>
          <p>
            Your account is currently pending approval. You'll receive access once
            the administrator reviews and approves your account.
          </p>
          <p className="email-info">
            Registered email: <strong>{currentUser?.email}</strong>
          </p>
        </div>

        <div className="pending-divider" />

        {/* Message form */}
        <div className="pending-contact">
          <h3>Send a message to ZeppelinDG</h3>
          <p className="pending-contact-hint">Have questions? Let us know how you heard about us or anything else.</p>

          {sent ? (
            <div className="message-sent">
              <CheckCircle size={20} />
              Message sent! We'll get back to you soon.
            </div>
          ) : (
            <>
              <textarea
                className="pending-textarea"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Write your message here..."
                rows={4}
                disabled={sending}
              />

              {file && (
                <div className="pending-file-preview">
                  <Paperclip size={14} />
                  <span>{file.name}</span>
                  <button onClick={handleRemoveFile} className="remove-file-btn">
                    <X size={14} />
                  </button>
                </div>
              )}

              {sending && uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress">
                  <div className="upload-bar" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}

              <div className="pending-form-actions">
                <button
                  className="btn-attach"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                >
                  <Paperclip size={16} />
                  Attach file
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn-send-message"
                  onClick={handleSend}
                  disabled={sending || (!message.trim() && !file)}
                >
                  {sending
                    ? <><Loader size={16} className="spinner" />Sending...</>
                    : <><Send size={16} />Send Message</>
                  }
                </button>
              </div>
            </>
          )}
        </div>

        <div className="pending-divider" />

        <button onClick={handleSignOut} className="signout-button">
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default PendingApproval;

/* ADD TO PendingApproval.css — append these styles: */
